'use client';

import { c as _c } from "react/compiler-runtime";
import type { NegociacaoTemperature } from '@/features/negociacoes';
import { getTemperatureIcon, getTemperatureMeta, temperatureOptions } from '@/features/negociacoes';
import * as RadixPopover from '@radix-ui/react-popover';
import { FeatherInfo, FeatherThermometer, FeatherX } from '@subframe/core';
import styles from './TemperaturaInfoPopover.module.css';
interface TemperaturaInfoPopoverProps {
  temperature: string;
}
const temperatureTones: Record<NegociacaoTemperature, {
  card: string;
  icon: string;
  bar: string;
}> = {
  Fria: {
    card: 'border-information-color-support-info-200 bg-information-color-support-info-50',
    icon: 'text-information-color-support-info-700',
    bar: 'bg-information-color-support-info-500'
  },
  Morna: {
    card: 'border-warning-200 bg-warning-50',
    icon: 'text-warning-700',
    bar: 'bg-warning-500'
  },
  Quente: {
    card: 'border-error-200 bg-error-50',
    icon: 'text-error-700',
    bar: 'bg-error-500'
  }
};
const temperatureScale = [...temperatureOptions].reverse();
export function TemperaturaInfoPopover(t0) {
  const $ = _c(71);
  const {
    temperature
  } = t0;
  let T0;
  let T1;
  let T2;
  let currentMeta;
  let currentTemperature;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== temperature) {
    currentTemperature = temperatureOptions.find(option => option === temperature);
    currentMeta = currentTemperature ? getTemperatureMeta(currentTemperature) : null;
    T2 = RadixPopover.Root;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <RadixPopover.Trigger asChild={true}><button type="button" aria-label={"Entender a temperatura da negocia\xE7\xE3o"} className="group border-brand-100 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 focus-visible:ring-brand-200 data-[state=open]:border-brand-300 data-[state=open]:bg-brand-100 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-solid shadow-sm transition-[background-color,border-color,box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-md focus-visible:ring-4 focus-visible:outline-none active:translate-y-0 active:scale-[0.98] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"><FeatherInfo className="h-3.5 w-3.5" /></button></RadixPopover.Trigger>;
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    T1 = RadixPopover.Portal;
    T0 = RadixPopover.Content;
    t7 = "bottom";
    t8 = "center";
    t9 = 10;
    t10 = 12;
    t11 = "temperatura-popover-title";
    t12 = "temperatura-popover-description";
    t13 = `${styles.popover} z-[10010] max-h-[calc(100vh-2rem)] w-[380px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-[16px] border border-solid border-neutral-200 bg-white shadow-2xl`;
    t3 = "from-brand-600 to-brand-900 relative overflow-hidden bg-gradient-to-br px-4 pt-4 pb-5 text-white";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-8 h-36 w-36 rounded-full border border-white/15" />;
      t5 = <div aria-hidden="true" className="pointer-events-none absolute -top-4 -right-1 h-24 w-24 rounded-full border border-white/15" />;
      $[21] = t4;
      $[22] = t5;
    } else {
      t4 = $[21];
      t5 = $[22];
    }
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-white/15"><FeatherThermometer className="h-5 w-5" /></span>;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="min-w-0 flex-1"><p className="text-[10px] font-semibold tracking-[0.12em] text-white/70 uppercase">Leitura da negociação</p><h3 id="temperatura-popover-title" className="text-body-bold font-body-bold mt-0.5">Temperatura</h3><p id="temperatura-popover-description" className="text-caption mt-1 text-white/80">Uma indicação da chance de acordo neste momento.</p></div>;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="relative flex items-start gap-3">{t15}{t16}<RadixPopover.Close asChild={true}><button type="button" aria-label={"Fechar explica\xE7\xE3o da temperatura"} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:ring-4 focus-visible:ring-white/30 focus-visible:outline-none"><FeatherX className="h-4 w-4" /></button></RadixPopover.Close></div>;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    t1 = "relative mt-4 flex items-center gap-2 rounded-[12px] border border-white/20 bg-white/10 px-3 py-2.5";
    t2 = currentTemperature && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20">{getTemperatureIcon(currentTemperature)}</span>;
    $[0] = temperature;
    $[1] = T0;
    $[2] = T1;
    $[3] = T2;
    $[4] = currentMeta;
    $[5] = currentTemperature;
    $[6] = t1;
    $[7] = t10;
    $[8] = t11;
    $[9] = t12;
    $[10] = t13;
    $[11] = t14;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[1];
    T1 = $[2];
    T2 = $[3];
    currentMeta = $[4];
    currentTemperature = $[5];
    t1 = $[6];
    t10 = $[7];
    t11 = $[8];
    t12 = $[9];
    t13 = $[10];
    t14 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="text-[10px] font-medium tracking-wide text-white/70 uppercase">Classificação atual</p>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = currentTemperature ?? temperature;
  const t17 = currentMeta ? ` · ${currentMeta.description}` : "";
  let t18;
  if ($[27] !== t16 || $[28] !== t17) {
    t18 = <div className="min-w-0">{t15}<p className="text-body-bold font-body-bold">{t16}{t17}</p></div>;
    $[27] = t16;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t1 || $[31] !== t18 || $[32] !== t2) {
    t19 = <div className={t1}>{t2}{t18}</div>;
    $[30] = t1;
    $[31] = t18;
    $[32] = t2;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19 || $[35] !== t3 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6) {
    t20 = <div className={t3}>{t4}{t5}{t6}{t19}</div>;
    $[34] = t19;
    $[35] = t3;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <p className="text-caption-bold font-caption-bold text-subtext-color tracking-wide uppercase">Como interpretar</p>;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== currentTemperature) {
    t22 = temperatureScale.map(option_0 => {
      const meta = getTemperatureMeta(option_0);
      const tone = temperatureTones[option_0];
      const isCurrent = option_0 === currentTemperature;
      return <div key={option_0} className={`flex min-w-0 flex-col items-center gap-1.5 rounded-[12px] border border-solid px-2 py-2.5 text-center ${isCurrent ? `${tone.card} shadow-sm` : "border-neutral-200 bg-neutral-50"}`}><span className={`${tone.icon} flex h-7 w-7 items-center justify-center`}>{getTemperatureIcon(option_0)}</span><span className="text-caption-bold font-caption-bold text-default-font">{option_0}</span><span className="text-subtext-color text-[11px] leading-[15px]">{meta.description}</span>{isCurrent && <span className="text-brand-700 text-[10px] font-semibold uppercase">Atual</span>}</div>;
    });
    $[41] = currentTemperature;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t22) {
    t23 = <div className="mt-3 grid grid-cols-3 gap-2">{t22}</div>;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== currentTemperature) {
    t24 = temperatureScale.map(option_1 => <span key={option_1} className={`${temperatureTones[option_1].bar} h-1.5 rounded-full ${option_1 === currentTemperature ? "opacity-100" : "opacity-45"}`} />);
    $[45] = currentTemperature;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t24) {
    t25 = <div className="mt-3 grid grid-cols-3 gap-2" aria-hidden="true">{t24}</div>;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t23 || $[50] !== t25) {
    t26 = <div className="px-4 pt-4 pb-3">{t21}{t23}{t25}</div>;
    $[49] = t23;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="text-caption text-subtext-color border-t border-solid border-neutral-100 px-4 py-3">Essa leitura pode mudar durante a conversa e não garante um resultado.</p>;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== T0 || $[54] !== t10 || $[55] !== t11 || $[56] !== t12 || $[57] !== t13 || $[58] !== t20 || $[59] !== t26 || $[60] !== t7 || $[61] !== t8 || $[62] !== t9) {
    t28 = <T0 side={t7} align={t8} sideOffset={t9} collisionPadding={t10} aria-labelledby={t11} aria-describedby={t12} className={t13}>{t20}{t26}{t27}</T0>;
    $[53] = T0;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t20;
    $[59] = t26;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== T1 || $[65] !== t28) {
    t29 = <T1>{t28}</T1>;
    $[64] = T1;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== T2 || $[68] !== t14 || $[69] !== t29) {
    t30 = <T2>{t14}{t29}</T2>;
    $[67] = T2;
    $[68] = t14;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  return t30;
}